/**
 * Path-mode lifecycle.
 *
 * The `mastpointsAlongLine` tool is a *selection* tool — it collects mast ids along a shared
 * line and, finished with a right click, hands off to path-mode, which is the persistent UI
 * **state** that owns:
 *   - the selected mast ids
 *   - the fetched section info / loadcase datasources / active loadcases
 *   - the hover-info tooltip subscription
 *
 * Crucially, path-mode is **not** a tool: it doesn't lock Cesium input. Normal entity
 * selection, the Toolbar, and other map tools all stay available while the path is active.
 * The section data simply persists until the user explicitly exits.
 *
 * State lives in `path-mode-state.svelte.ts` as a runes-based singleton. This module is the
 * orchestration layer that ties together that state with the Cesium viewer, IDB cache,
 * broadcast channel and DOM listeners.
 */

import { get } from 'svelte/store';

import {
	activeTool,
	isAnyToolEnabled,
	type ToolPropertyKey
} from '../map-tools-store';
import { toggleEscapeToolListener } from '../map-tools';
import { confirmModal } from '$lib/components/organisms/confirmModal/confirmModal';
import { clearLoadCases } from './pathToolLoadCases.svelte';
import { activateHoverInfo, deactivateHoverInfo } from './pathToolHoverInfo.svelte';
import { runManualMapRefresh } from '../map-utils';
import { dismissOverlayMessageByKey } from '$lib/stores/cesium-overlay-messages-store';
import { pathModeState } from './path-mode-state.svelte';
// Imported for side-effect: installs module-level `$effect.root` watchers
// that auto-fetch `/info` and apply section colours + mast labels reactively.
import './path-mode-effects.svelte';
// Imported for side-effect: installs a `$effect.root` watcher that loads the
// section's TerrainProfile packets into the shared distance-analysis datasource
// and drives the terrain-offset lines while "Vis avstander" is active.
import './pathToolTerrainProfiles.svelte';

/**
 * Shared dedupe key for the overlay message that warns the user that section
 * data is out-of-date after a montagestretch save. Defined here so the
 * path-mode lifecycle can clean it up on exit without coupling to the panel.
 */
export const PATH_MODE_STALE_DATA_OVERLAY_KEY = 'path-mode-section-data-stale';

let installed = false;
/**
 * Tools that mutate underlying entities while path-mode is active. Finishing
 * one of these triggers a section-info refresh because the selected masts
 * may have changed. Other Toolbar tools (e.g. `createTerrainProfileFromPoles`)
 * are read-only with respect to mastpoints/lines, so we leave path-mode alone
 * when they end — otherwise the chip would briefly snap to "0 master"
 * while a fresh /info request reloaded.
 */
const ENTITY_EDITING_SUBTOOLS: ReadonlySet<ToolPropertyKey> = new Set([
	'rotateMastpoint',
	'quickMoveMastPoint',
	'quickMovePole',
	'quickMovePoleConstruction',
	'quickAdjustMastPointHeight'
]);

/**
 * The most recently observed entity-editing sub-tool. Reset when path-mode
 * itself toggles off, or after we run the resume side-effects.
 */
let lastEditingSubtool: ToolPropertyKey | null = null;

// ── Public API ──────────────────────────────────────────────────────────────

export type PathModeActivationOptions = {
	/** Ids of the masts selected with the path tool, in order. */
	mastIds: number[];
	/**
	 * Initial state of the per-section "Vis avstander" toggle. Defaults to `false`.
	 */
	showDistances?: boolean;
};

/**
 * Called by the `mastpointsAlongLine` tool when the user finishes the path with a right
 * click. Flips the path-mode state on; the reactor in `path-mode-effects` then fetches
 * `Section/calculate/info` for the masts.
 */
export function activatePathMode(opts: PathModeActivationOptions): void {
	// Replacing a previous path: drop its load-case datasources and listeners first.
	if (pathModeState.active) deactivatePathMode();

	pathModeState.active = true;
	pathModeState.mastIds = [...opts.mastIds];
	pathModeState.sectionInfo = null;
	pathModeState.sectionInfoError = null;
	pathModeState.sectionInfoLoading = false;
	pathModeState.activeLoadCases = [];
	pathModeState.showDistances = opts.showDistances ?? false;
	pathModeState.panelMinimized = false;

	activateHoverInfo();

	// Esc → confirm-and-exit. Routed via a dedicated listener (not the shared
	// tool-Esc handler) because path-mode isn't a tool — the shared listener
	// would never fire for it.
	toggleEscapeToolListener({ toggleOn: false });
	document.addEventListener('keydown', handlePathModeEsc, false);

	ensureSubtoolWatcher();
}

/** Confirm + deactivate. Wired to Esc and the "Lukk" button. */
export function requestExitPathMode(): void {
	confirmModal(
		'Er du sikker på at du vil gå ut av trasémodus. Utvalget av mastepunkt vil bli fjernet og ulagret data vil gå tapt.',
		() => {
			deactivatePathMode();
		},
		undefined,
		{
			labels: {
				confirm: 'Ja, gå ut av trasémodus',
				cancel: 'Nei, ta meg tilbake.',
				title: 'Avslutt trasémodus'
			},
			type: 'warning'
		}
	);
}

/** Hard exit — clear the section data and state. */
export function deactivatePathMode(): void {
	clearLoadCases();
	deactivateHoverInfo();

	pathModeState.reset();

	dismissOverlayMessageByKey(PATH_MODE_STALE_DATA_OVERLAY_KEY);
	document.removeEventListener('keydown', handlePathModeEsc, false);
}

// ── Minimize / maximize the side panel ─────────────────────────────────────

/**
 * Re-exported thin wrappers around the state mutators so callers don't need
 * to know whether the panel state lives on the class or somewhere else.
 */
export function minimizePathPanel(): void {
	pathModeState.minimize();
}

export function maximizePathPanel(): void {
	pathModeState.maximize();
}

// ── Sub-tool integration ────────────────────────────────────────────────────

/**
 * Called when the user activates a sub-tool (rotate / move / adjust-height)
 * from the Toolbar while path-mode is active. Suppresses hover-info so it
 * doesn't compete with the tool's own overlay; the sub-tool watcher restores
 * everything when the tool deactivates.
 */
export function pausePathModeForSubtool(): void {
	deactivateHoverInfo();
	// The path panel hides automatically while any tool is active (its
	// mount in +page.svelte is in the no-tool branch).
}

/**
 * Called after a sub-tool deactivates. Re-enables hover-info — nothing else.
 *
 * Mutations are the only thing that should mark the cache stale and wipe the section
 * info, and that already happens at the source: every entity-editing mutation routes
 * through `handleContextualizedCzmlResponse` in `map-utils`, which does it exactly when
 * the affected masts are among the selected ones.
 */
function resumePathModeAfterSubtool(): void {
	activateHoverInfo();
}

/**
 * Reload the underlying map data and refetch section info, while keeping the
 * selected masts (and the panel) intact. Used by the stale-data overlay
 * shown after a montagestretch save.
 *
 * Section info refetch is triggered indirectly by clearing `sectionInfo` —
 * the reactor in `path-mode-effects` then re-issues `/info`.
 */
export async function refreshPathModeContext(): Promise<void> {
	if (!pathModeState.active) return;
	await runManualMapRefresh();
	clearLoadCases();
	pathModeState.sectionInfo = null;
	pathModeState.sectionInfoError = null;
	pathModeState.sectionInfoLoading = false;
	pathModeState.activeLoadCases = [];
}

/**
 * Module-local watcher: when an *entity-editing* sub-tool finishes while
 * path-mode is active, run the resume side-effects. Non-editing tools (terrain
 * profiles, layer pickers, etc.) are ignored.
 */
function ensureSubtoolWatcher(): void {
	if (installed) return;
	installed = true;

	activeTool.subscribe(($activeTool) => {
		if (!pathModeState.active) {
			lastEditingSubtool = null;
			return;
		}
		if ($activeTool && ENTITY_EDITING_SUBTOOLS.has($activeTool)) {
			// Clears a lingering tooltip and marks hover inactive while the tool owns the mouse.
			if (lastEditingSubtool !== $activeTool) pausePathModeForSubtool();
			lastEditingSubtool = $activeTool;
			return;
		}
		if (!$activeTool && lastEditingSubtool) {
			lastEditingSubtool = null;
			// This subscriber fires synchronously inside the store update that ends the tool,
			// BEFORE the tool's own teardown runs removeInputAction(MOUSE_MOVE). Re-arm on the
			// next tick so that teardown cannot wipe the handler we install.
			setTimeout(() => {
				if (pathModeState.active && !get(isAnyToolEnabled)) resumePathModeAfterSubtool();
			}, 0);
		}
	});
}

// ── Internal ────────────────────────────────────────────────────────────────

function handlePathModeEsc(e: KeyboardEvent): void {
	if (e.key !== 'Escape' && e.key !== 'Esc') return;
	if (!pathModeState.active) return;
	// Defer to any active tool's own Esc handler; the watcher resumes us
	// when the tool deactivates.
	if (get(isAnyToolEnabled)) return;
	requestExitPathMode();
}
